import React, { createContext, useContext, useState } from 'react';
import { BreadcrumbItem } from '../components/Breadcrumb/Breadcrumb';
import { Home } from '@mui/icons-material';

// Default breadcrumbs always start with Home
const defaultBreadcrumbs: BreadcrumbItem[] = [
  { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
];

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
  updateBreadcrumb: (index: number, item: Partial<BreadcrumbItem>) => void;
  breadcrumbActions: React.ReactNode;
  setBreadcrumbActions: (actions: React.ReactNode) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType>({
  breadcrumbs: defaultBreadcrumbs,
  setBreadcrumbs: (items: BreadcrumbItem[]) => {
    console.warn('setBreadcrumbs was called outside of provider');
  },
  updateBreadcrumb: (index: number, item: Partial<BreadcrumbItem>) => {
    console.warn('updateBreadcrumb was called outside of provider');
  },
  breadcrumbActions: null,
  setBreadcrumbActions: (actions: React.ReactNode) => {
    console.warn('setBreadcrumbActions was called outside of provider');
  },
});

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>(defaultBreadcrumbs);
  const [breadcrumbActions, setBreadcrumbActions] = useState<React.ReactNode>(null);

  const updateBreadcrumb = (index: number, item: Partial<BreadcrumbItem>) => {
    if (index < 0 || index >= breadcrumbs.length) return;

    const newBreadcrumbs = [...breadcrumbs];
    newBreadcrumbs[index] = { ...newBreadcrumbs[index], ...item };
    setBreadcrumbs(newBreadcrumbs);
  };

  return (
    <BreadcrumbContext.Provider
      value={{
        breadcrumbs,
        setBreadcrumbs,
        updateBreadcrumb,
        breadcrumbActions,
        setBreadcrumbActions,
      }}
    >
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = () => useContext(BreadcrumbContext);
